/**
 * Pure serializer: take the project entity + per-project data → a compact,
 * stable JSON context the LLM can reason over.
 *
 * "Stable" matters for caching: same inputs must produce byte-identical
 * output so contextHash() is deterministic.
 */

import type { Budget, Phase, Project } from "@shared/types";
import { totals as computeBudgetTotals } from "@domain/budget/totals";

export interface ProjectLLMContext {
  project: {
    id: string;
    name: string;
    address: string;
    sqFt?: {
      current?: number;
      expansion?: number;
      total?: number;
    };
    spaces?: Array<{ name: string; sqFt: number }>;
  };
  budget: {
    categories: Array<{
      id: number;
      name: string;
      planned: number; // `estimated` internally, "Planned" in UI
      actual: number;
      variance: number;
      notes?: string;
    }>;
    totals: {
      planned: number;
      actual: number;
      variance: number;
      costPerSqFt: number | null;
    };
  };
  timeline: {
    phases: Array<{
      id: number;
      name: string;
      status?: string;
      startDate?: string;
      endDate?: string;
      durationWeeks?: number;
      tasksCompleted: number;
      tasksTotal: number;
    }>;
  };
  contractors: Array<{
    name: string;
    role?: string;
    phone?: string;
    email?: string;
    notes?: string;
  }>;
  floorplans: Array<{ name: string; ext: string; size: number }>;
  designItemCount: number;
  feedbackOpenCount: number;
  permits: {
    itemsChecked: number;
  };
}

export interface ProjectContextInput {
  project: Project;
  budget: Budget;
  phases: Phase[];
  contractors: Array<Record<string, unknown>>;
  floorplans: Array<{ name: string; ext: string; size: number }>;
  designItems: Array<Record<string, unknown>>;
  feedbackTickets: Array<{ status?: string }>;
  permitsChecklist: Record<string, boolean>;
  /** Optional metadata that rides along (e.g., sqft from plan data). */
  sqFt?: {
    current?: number;
    expansion?: number;
    total?: number;
  };
  spaces?: Array<{ name: string; sqFt: number }>;
}

export function buildProjectContext(
  input: ProjectContextInput
): ProjectLLMContext {
  const totals = computeBudgetTotals(input.budget);
  const totalSqFt =
    input.sqFt?.total ?? (input.sqFt?.current ?? 0) + (input.sqFt?.expansion ?? 0);

  const costPerSqFt = totalSqFt > 0 ? Math.round(totals.estimated / totalSqFt) : null;

  return {
    project: {
      id: input.project.id,
      name: input.project.name,
      address: input.project.address,
      ...(input.sqFt ? { sqFt: input.sqFt } : {}),
      ...(input.spaces ? { spaces: input.spaces } : {}),
    },
    budget: {
      categories: input.budget.map((c) => ({
        id: c.id,
        name: c.name,
        planned: c.estimated,
        actual: c.actual,
        variance: c.estimated - c.actual,
        ...(c.notes ? { notes: c.notes } : {}),
      })),
      totals: {
        planned: totals.estimated,
        actual: totals.actual,
        variance: totals.variance,
        costPerSqFt,
      },
    },
    timeline: {
      phases: input.phases.map((p) => {
        const tasks = p.tasks ?? [];
        const completed = tasks.filter((t) => t.completed).length;
        const phaseAny = p as unknown as Record<string, unknown>;
        return {
          id: p.id,
          name: p.name,
          ...(typeof phaseAny.status === "string" ? { status: phaseAny.status } : {}),
          ...(typeof phaseAny.startDate === "string" ? { startDate: phaseAny.startDate as string } : {}),
          ...(typeof phaseAny.endDate === "string" ? { endDate: phaseAny.endDate as string } : {}),
          ...(p.durationWeeks != null ? { durationWeeks: p.durationWeeks } : {}),
          tasksCompleted: completed,
          tasksTotal: tasks.length,
        };
      }),
    },
    contractors: input.contractors.map((c) => {
      const typed = c as {
        name?: string;
        role?: string;
        phone?: string;
        email?: string;
        notes?: string;
      };
      return {
        name: typed.name ?? "Unknown",
        ...(typed.role ? { role: typed.role } : {}),
        ...(typed.phone ? { phone: typed.phone } : {}),
        ...(typed.email ? { email: typed.email } : {}),
        ...(typed.notes ? { notes: typed.notes } : {}),
      };
    }),
    floorplans: input.floorplans.map((f) => ({
      name: f.name,
      ext: f.ext,
      size: f.size,
    })),
    designItemCount: input.designItems.length,
    feedbackOpenCount: input.feedbackTickets.filter(
      (t) => (t.status ?? "open") === "open"
    ).length,
    permits: {
      itemsChecked: Object.values(input.permitsChecklist).filter(Boolean).length,
    },
  };
}

/**
 * Deterministic hash of the serialized context. Used as cache key for
 * projectReviews — same project state → same hash → cached review is valid.
 *
 * FNV-1a 64-bit (as hex) — no crypto libs needed, fast, plenty of
 * collision resistance for this use case.
 */
export function hashContext(
  ctx: ProjectLLMContext,
  rejectedTitles: string[] = []
): string {
  const sortedRejections = [...rejectedTitles].sort();
  const serialized = JSON.stringify({ ctx, rejected: sortedRejections });
  let h = 0xcbf29ce484222325n;
  const prime = 0x100000001b3n;
  for (let i = 0; i < serialized.length; i++) {
    h ^= BigInt(serialized.charCodeAt(i));
    h = (h * prime) & 0xffffffffffffffffn;
  }
  return h.toString(16).padStart(16, "0");
}
